import React, { useState } from 'react';
import { X, Trash2, Plus, Minus, ShoppingBag, ArrowRight, ShieldCheck, Tag } from 'lucide-react';
import { useCart } from '../../context/CartContext';
import { STORE_CONFIG } from '../../config/storeConfig';
import { FreeShippingBar } from '../common/FreeShippingBar';
import { FlavorType } from '../../types/store';
import { getProductMainImage, ASSET_IMAGES } from '../../data/products';

interface CartDrawerProps {
  onNavigateToCheckout: () => void;
  onNavigateToShop: () => void;
}

export const CartDrawer: React.FC<CartDrawerProps> = ({
  onNavigateToCheckout,
  onNavigateToShop,
}) => {
  const {
    items,
    isCartOpen,
    closeCart,
    removeFromCart,
    updateQuantity,
    updateItemFlavor,
    subtotal,
    shippingFee,
    discount,
    total,
    totalItemsCount,
    activePromoCode,
    applyPromoCode,
    removePromoCode,
  } = useCart();

  const [promoInput, setPromoInput] = useState('');
  const [promoMessage, setPromoMessage] = useState<{ text: string; isError: boolean } | null>(null);

  if (!isCartOpen) return null;

  const handleApplyPromo = (e: React.FormEvent) => {
    e.preventDefault();
    if (!promoInput.trim()) return;
    const result = applyPromoCode(promoInput);
    setPromoMessage({ text: result.message, isError: !result.success });
    if (result.success) {
      setPromoInput('');
    }
  };

  const flavorsList: FlavorType[] = ['Vanilla', 'Mango', 'Chocolate', 'Strawberry'];

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/60 backdrop-blur-xs transition-opacity duration-300"
        onClick={closeCart}
        aria-hidden="true"
      />

      <div className="fixed inset-y-0 right-0 max-w-full flex">
        <div className="w-screen max-w-full sm:max-w-md bg-white shadow-2xl flex flex-col justify-between h-full">
          {/* Header */}
          <div className="p-4 sm:p-5 border-b border-slate-100 flex items-center justify-between bg-white z-10">
            <div className="flex items-center gap-2">
              <ShoppingBag className="w-5 h-5 text-[#191975]" />
              <h2 className="text-lg font-bold text-[#172554]">
                Your Shopping Bag ({totalItemsCount})
              </h2>
            </div>
            <button
              id="close-cart-btn"
              onClick={closeCart}
              className="p-2 rounded-full text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
              aria-label="Close cart"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Free Shipping Tracker */}
          <div className="px-4 py-3 bg-[#F8FAFC] border-b border-slate-100">
            <FreeShippingBar compact />
          </div>

          {/* Cart Items List or Empty State */}
          <div className="flex-1 overflow-y-auto p-4 sm:p-5 space-y-4">
            {items.length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center text-center py-12 px-4 space-y-4">
                <div className="w-20 h-20 bg-blue-50 rounded-full flex items-center justify-center text-[#191975]">
                  <ShoppingBag className="w-10 h-10 opacity-70" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-[#172554]">Your cart is empty</h3>
                  <p className="text-xs text-slate-500 mt-1 max-w-xs">
                    Explore our healthy weight gain formulas and nutrition supplements.
                  </p>
                </div>
                <button
                  id="empty-cart-shop-btn"
                  onClick={() => {
                    closeCart();
                    onNavigateToShop();
                  }}
                  className="bg-[#191975] hover:bg-[#14145c] text-white px-6 py-2.5 rounded-xl font-bold text-xs uppercase tracking-wider transition-colors shadow-sm"
                >
                  Start Shopping
                </button>
              </div>
            ) : (
              items.map((item) => (
                <div
                  key={item.id}
                  className="bg-white rounded-xl border border-slate-100 p-3.5 flex gap-3.5 shadow-xs relative"
                >
                  {/* Thumbnail */}
                  <div className="w-20 h-20 bg-slate-50 rounded-lg overflow-hidden shrink-0 border border-slate-100 flex items-center justify-center">
                    <img
                      src={item.image || getProductMainImage({ flavor: item.flavor, id: item.productId })}
                      alt={item.title}
                      width="80"
                      height="80"
                      loading="lazy"
                      decoding="async"
                      className="w-full h-full object-cover"
                      referrerPolicy="no-referrer"
                      onError={(e) => {
                        const fallback = getProductMainImage({ flavor: item.flavor, id: item.productId });
                        (e.currentTarget as HTMLImageElement).src = fallback;
                      }}
                    />
                  </div>

                  {/* Details */}
                  <div className="flex-1 flex flex-col justify-between min-w-0">
                    <div className="pr-6">
                      <h4 className="text-xs sm:text-sm font-bold text-[#172554] truncate">
                        {item.title}
                      </h4>
                      <p className="text-[11px] text-slate-500 mt-0.5">
                        Net Wt: {item.weight}
                      </p>

                      {/* Flavor switch options */}
                      {!item.id.includes('bundle') && (
                        <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                          <span className="text-[10px] font-semibold text-slate-400">Flavor:</span>
                          {flavorsList.map((fl) => (
                            <button
                              key={fl}
                              onClick={() => updateItemFlavor(item.id, fl)}
                              className={`text-[9px] font-bold px-1.5 py-0.5 rounded border ${
                                item.flavor === fl
                                  ? 'bg-[#191975] text-white border-[#191975]'
                                  : 'bg-slate-50 text-slate-600 border-slate-200 hover:border-slate-300'
                              }`}
                            >
                              {fl}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="flex items-center justify-between mt-3 pt-2 border-t border-slate-100">
                      {/* Quantity Selector */}
                      <div className="flex items-center border border-slate-200 rounded-lg overflow-hidden bg-slate-50">
                        <button
                          onClick={() => updateQuantity(item.id, item.quantity - 1)}
                          className="p-1 text-slate-600 hover:bg-slate-200 transition-colors"
                          aria-label="Decrease quantity"
                        >
                          <Minus className="w-3 h-3" />
                        </button>
                        <span className="px-2.5 text-xs font-bold text-[#172554]">
                          {item.quantity}
                        </span>
                        <button
                          onClick={() => updateQuantity(item.id, item.quantity + 1)}
                          className="p-1 text-slate-600 hover:bg-slate-200 transition-colors"
                          aria-label="Increase quantity"
                        >
                          <Plus className="w-3 h-3" />
                        </button>
                      </div>

                      {/* Price */}
                      <div className="text-right">
                        <div className="text-xs sm:text-sm font-extrabold text-[#172554]">
                          {STORE_CONFIG.formatPrice(item.price * item.quantity)}
                        </div>
                        {item.quantity > 1 && (
                          <div className="text-[10px] text-slate-400">
                            {STORE_CONFIG.formatPrice(item.price)} each
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Remove Item Button */}
                    <button
                      onClick={() => removeFromCart(item.id)}
                      className="absolute top-3 right-3 text-slate-400 hover:text-rose-600 p-1 transition-colors"
                      title="Remove item"
                      aria-label="Remove item"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Footer & Checkout Action */}
          {items.length > 0 && (
            <div className="p-4 sm:p-5 border-t border-slate-200 bg-white space-y-3 shadow-lg">
              {/* Promo Code Form */}
              <div>
                {activePromoCode ? (
                  <div className="flex items-center justify-between bg-emerald-50 text-emerald-800 text-xs px-3 py-2 rounded-lg border border-emerald-200">
                    <span className="flex items-center gap-1.5 font-semibold">
                      <Tag className="w-3.5 h-3.5 text-emerald-600" />
                      Promo: <strong>{activePromoCode}</strong> applied
                    </span>
                    <button
                      onClick={removePromoCode}
                      className="text-xs font-bold text-rose-600 hover:underline"
                    >
                      Remove
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleApplyPromo} className="flex gap-2">
                    <input
                      type="text"
                      value={promoInput}
                      onChange={(e) => setPromoInput(e.target.value)}
                      placeholder="Discount Code (e.g. HEALTH10)"
                      className="flex-1 text-xs border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:border-[#191975]"
                    />
                    <button
                      type="submit"
                      className="bg-slate-100 hover:bg-slate-200 text-[#172554] text-xs font-bold px-3 py-2 rounded-lg transition-colors"
                    >
                      Apply
                    </button>
                  </form>
                )}

                {promoMessage && (
                  <p
                    className={`text-[11px] mt-1 ${
                      promoMessage.isError ? 'text-rose-600' : 'text-emerald-600'
                    }`}
                  >
                    {promoMessage.text}
                  </p>
                )}
              </div>

              {/* Subtotal & Totals breakdown */}
              <div className="space-y-1.5 text-xs text-slate-600 pt-1">
                <div className="flex justify-between">
                  <span>Subtotal</span>
                  <span className="font-bold text-[#172554]">{STORE_CONFIG.formatPrice(subtotal)}</span>
                </div>

                {discount > 0 && (
                  <div className="flex justify-between text-emerald-700 font-semibold">
                    <span>Discount</span>
                    <span>-{STORE_CONFIG.formatPrice(discount)}</span>
                  </div>
                )}

                <div className="flex justify-between">
                  <span>Shipping (Nationwide Pakistan)</span>
                  <span className="font-bold">
                    {shippingFee === 0 ? (
                      <span className="text-emerald-600 uppercase font-extrabold">FREE</span>
                    ) : (
                      STORE_CONFIG.formatPrice(shippingFee)
                    )}
                  </span>
                </div>

                <div className="flex justify-between text-sm sm:text-base font-extrabold text-[#172554] pt-2 border-t border-slate-100">
                  <span>Estimated Total</span>
                  <span>{STORE_CONFIG.formatPrice(total)}</span>
                </div>
              </div>

              {/* Checkout CTA */}
              <button
                id="drawer-checkout-btn"
                onClick={() => {
                  closeCart();
                  onNavigateToCheckout();
                }}
                className="w-full bg-[#191975] hover:bg-[#14145c] text-white py-3.5 px-4 rounded-xl font-extrabold text-sm uppercase tracking-wider flex items-center justify-center gap-2 transition-colors shadow-md hover:shadow-lg active:scale-[0.99]"
              >
                <span>PROCEED TO CHECKOUT</span>
                <ArrowRight className="w-4 h-4" />
              </button>

              <div className="flex items-center justify-center gap-2 text-[11px] text-slate-500 font-medium pt-1">
                <ShieldCheck className="w-4 h-4 text-emerald-600 shrink-0" />
                <span>Cash on Delivery across Pakistan • 100% Genuine</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
